import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import Fuse from 'fuse.js';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { chunk } from 'remeda';
import { BehaviorSubject, combineLatest, interval, Observable, Subject } from 'rxjs';
import { filter, map, startWith, take, takeUntil } from 'rxjs/operators';
import {
  DriverDataBrief,
  LohiDashboardService,
  LoHiSandiLoadNeedingAdminApproval,
} from '../../dashboard-2025/lohi-dashboard.service';
import { AuthService, LoHiUserInfo } from '../../root/auth.service';
import { FeatureFlagService } from '../../root/feature-flags.service';
import { RouterStateService } from '../../root/router-state.service';
import { LoadApprovalDialogComponent } from '../../shared/load-approval-dialog/load-approval-dialog.component';
import { DriversHitchCalendarDay, OwnerDashboardStats, TourRevenue } from '../../types';
import { fuse } from '../../utilities/fuse';
import { OwnerDashboardService } from '../owner-dashboard.service';
import {
  DriversForTarget,
  Hazard,
  MapCorners,
  MigrationMappableDriver,
  MigrationMappableTarget,
  MigrationMappableTargetAccepted,
  MigrationMapService,
  SandId,
} from './migration-map/migration-map.service';

const searchOptions: Fuse.IFuseOptions<MigrationMappableDriver> = {
  distance: 100,
  keys: ['name', 'truckName', 'trailerName'],
  location: 0,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.1,
};

@Component({
  selector: 'lohi-owner-dashboard',
  templateUrl: './owner-dashboard.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OwnerDashboardComponent implements OnInit, OnDestroy {
  @ViewChild('revenueCalculation') private revenueCalculationModal: TemplateRef<any>;
  @ViewChild('driverSelection') private driverSelectionModal: TemplateRef<any>;
  public searchForm = new FormControl(null);
  public stats$: Observable<OwnerDashboardStats>;
  public searchableDriverList$: Observable<MigrationMappableDriver[]>;
  public groupedDrivers$: Observable<{ name: string; data: MigrationMappableDriver[] }[]>;
  public targets$: Observable<MigrationMappableTarget[]>;
  public hazards$: Observable<Hazard[]>;
  public dismissedTargets$: Observable<MigrationMappableTarget[]>;
  public acceptedTargets$: Observable<MigrationMappableTargetAccepted[]>;
  public drivers$: Observable<MigrationMappableDriver[]>;
  public driversHitchCalendarWeeks$$ = new BehaviorSubject<DriversHitchCalendarDay[][]>([]);
  public driverHitchCalenderData: DriversHitchCalendarDay[];
  private destroy$$ = new Subject();
  public selectedTarget: MigrationMappableTarget;
  public targetSection = 'listOfTargets';
  public checkboxDriverIds = new Set<string>();
  public checkboxDriverNames = new Set<string>();
  public confirmedDrivers: DriversForTarget = {
    sandDemandSnapshotId: 0,
    drivers: [],
  };
  public disableConfirmButton = true;
  public disableCheckbox = false;
  public trailerTypes = new Set<string>();
  public noShowYesterday = 0;
  public bannerLoadsCount$: Observable<number>;

  public exportGroup: FormGroup;

  public tourRev$: Observable<TourRevenue>;
  public userInfo$: Observable<LoHiUserInfo>;
  public loadsNeedingAcceptance: LoHiSandiLoadNeedingAdminApproval[] = [];
  public adminAcceptanceAvailableDrivers: DriverDataBrief[] = [];

  constructor(
    private migrationMapService: MigrationMapService,
    private dashboardService: OwnerDashboardService,
    private lohiDashboardService: LohiDashboardService,
    private featureFlagService: FeatureFlagService,
    private matDialog: MatDialog,
    private routerState: RouterStateService,
    private authService: AuthService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.loadLoadsNeedingAcceptance();
    this.bannerLoadsCount$ = this.dashboardService.shBannerLoadsCount$.pipe(
      map((value) => {
        if (value) {
          return value.count;
        }
      }),
    );
    const toSend: MapCorners = {
      corner_a: {
        longitude: -129.03,
        latitude: 46.36,
      },
      corner_b: {
        longitude: -57.51,
        latitude: 23.2,
      },
    };
    this.stats$ = this.dashboardService.stats$;
    this.targets$ = combineLatest([
      this.routerState.listenForQueryChange$('opportunity'),
      this.migrationMapService.migrationMapTargets$,
    ]).pipe(
      map(([opportunity, targets]) => {
        if (opportunity) {
          return targets.filter((target) => target.sandDemandSnapshotId === parseInt(opportunity, 10));
        }
        return targets;
      }),
    );
    this.sortTargets();
    this.drivers$ = this.migrationMapService.migrationMapDrivers$;
    this.dismissedTargets$ = this.migrationMapService.migrationMapTargetsDismissed$;
    this.acceptedTargets$ = this.migrationMapService.migrationMapTargetsAccepted$;
    this.hazards$ = this.migrationMapService.getHazards$(toSend);
    this.userInfo$ = this.authService.userInfo$;
    this.setupDrivers();
    this.setNoShowYesterday();
    interval(10000)
      .pipe(takeUntil(this.destroy$$))
      .subscribe(async () => {
        const isFlagActiveNow = await this.featureFlagService.isFlagActiveNow('migrationMapPollingData');
        if (isFlagActiveNow) {
          this.migrationMapService.triggerRefresh();
        }
      });

    this.tourRev$ = this.dashboardService.tourRevenue$;
    this.setupForm();
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public loadLoadsNeedingAcceptance(): void {
    this.lohiDashboardService.getLoadsNeedingAdminAcceptance$().subscribe({
      next: ({ loads, availableDrivers }) => {
        this.loadsNeedingAcceptance = loads ?? [];
        this.adminAcceptanceAvailableDrivers = availableDrivers ?? [];
        this.cdr.markForCheck();
      },
      error: (err) => console.error('Error loading loads needing acceptance:', err),
    });
  }

  public openLoadApprovalDialog(load: LoHiSandiLoadNeedingAdminApproval): void {
    const dialogRef = this.matDialog.open(LoadApprovalDialogComponent, {
      width: '520px',
      maxWidth: 'calc(100vw - 32px)',
      data: { load, availableDrivers: this.adminAcceptanceAvailableDrivers },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.loadLoadsNeedingAcceptance();
      }
    });
  }

  public trackByLoadId(index: number, load: LoHiSandiLoadNeedingAdminApproval): string {
    return load.id;
  }

  public setupDrivers() {
    this.searchableDriverList$ = combineLatest([
      this.drivers$.pipe(map(fuse(searchOptions))),
      this.searchForm.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([fused, searchTerm]) => {
        if (!fused) {
          return [];
        }
        if (!searchTerm) {
          return fused.data;
        }
        return fused.fuse.search(searchTerm).map((d) => d.item);
      }),
    );
    this.groupedDrivers$ = combineLatest([this.searchableDriverList$, this.userInfo$]).pipe(
      map(([drivers, userInfo]) => {
        const onLoad = { name: 'On Load', data: [] };
        const onShift = { name: 'On Shift', data: [] };
        const offShift = { name: 'Off Shift', data: [] };
        const onTour = { name: 'On Tour', data: [] };
        const offTour = { name: 'Off Tour', data: [] };
        for (const driver of drivers) {
          if (driver.loadId) {
            onLoad.data.push(driver);
          } else if (driver.onShift) {
            onShift.data.push(driver);
          } else if (driver.onTour) {
            onTour.data.push(driver);
          } else {
            offTour.data.push(driver);
            offShift.data.push(driver);
          }
        }
        const toReturn = [];
        onLoad.data.sort((a, b) => {
          if (new Date(a.lastWaypointTime).getTime() > new Date(b.lastWaypointTime).getTime()) {
            return -1;
          }
        });
        toReturn.push(onLoad);

        if (userInfo.enableTours) {
          onTour.data.sort((a, b) => {
            if (new Date(a.lastWaypointTime).getTime() > new Date(b.lastWaypointTime).getTime()) {
              return -1;
            }
          });
          offTour.data.sort((a, b) => {
            if (new Date(a.lastWaypointTime).getTime() > new Date(b.lastWaypointTime).getTime()) {
              return -1;
            }
          });
          toReturn.push(onTour);
        }

        if (!userInfo.isCompanyFiveFTourOnly) {
          onShift.data.sort((a, b) => {
            if (new Date(a.lastWaypointTime).getTime() > new Date(b.lastWaypointTime).getTime()) {
              return -1;
            }
          });
          toReturn.push(onShift);
          offShift.data.sort((a, b) => {
            if (new Date(a.lastWaypointTime).getTime() > new Date(b.lastWaypointTime).getTime()) {
              return -1;
            }
          });
          toReturn.push(offShift);
        }
        return toReturn;
      }),
    );
  }

  public onClickOpportunity(target) {
    this.selectedTarget = target;
    this.targetSection = 'targetDetails';
    this.checkboxDriverIds = new Set<string>();
    this.checkboxDriverNames = new Set<string>();
  }

  // send target id to server to update dismissed target list
  public async onClickNotInterested(snapShotId) {
    const toSend: SandId = {
      sandDemandSnapshotId: snapShotId,
    };
    try {
      await this.migrationMapService.dismissOpportunity(toSend);
    } catch (e) {
      console.error(e);
    }
    this.targets$ = this.migrationMapService.migrationMapTargets$;
    this.sortTargets();
    this.dismissedTargets$ = this.migrationMapService.migrationMapTargetsDismissed$;
    this.targetSection = 'listOfTargets';
  }

  public onClickSelectDriver() {
    this.targetSection = 'selectDrivers';
    this.disableConfirmButton = true;
  }

  public async onClickConfirmProject() {
    this.targetSection = 'projectConfirmed';
    this.confirmedDrivers.sandDemandSnapshotId = this.selectedTarget.sandDemandSnapshotId;
    for (const item of this.checkboxDriverIds) {
      this.confirmedDrivers.drivers.push({ driverId: item });
    }
    try {
      await this.migrationMapService.confirmDrivers(this.confirmedDrivers);
    } catch (e) {
      console.error(e);
    }
    this.targets$ = this.migrationMapService.migrationMapTargets$;
    this.sortTargets();
    this.acceptedTargets$ = this.migrationMapService.migrationMapTargetsAccepted$;
    this.dismissedTargets$ = this.migrationMapService.migrationMapTargetsDismissed$;
  }

  public navToDriver(driverInput: MigrationMappableDriver) {
    this.searchableDriverList$ = this.searchableDriverList$.pipe(
      map((drivers) => drivers.filter((driver) => driver.name === driverInput.name)),
      filter((items) => items && items.length > 0),
    );
  }

  public onCheckboxSelected(id: string, name: string, desired: number) {
    // store checked driver information
    if (this.checkboxDriverIds.has(id)) {
      this.checkboxDriverIds.delete(id);
    } else {
      this.checkboxDriverIds.add(id);
    }
    if (this.checkboxDriverNames.has(name)) {
      this.checkboxDriverNames.delete(name);
    } else {
      this.checkboxDriverNames.add(name);
    }
    // disable button when no driver is selected
    if (this.checkboxDriverIds.size === 0) {
      this.disableConfirmButton = true;
    } else {
      this.disableConfirmButton = false;
    }

    if (this.checkboxDriverIds.size > desired) {
      this.disableConfirmButton = true;
    }
  }

  public onClickAvailable() {
    this.targets$ = this.migrationMapService.migrationMapTargets$;
    this.sortTargets();
    this.acceptedTargets$ = this.migrationMapService.migrationMapTargetsAccepted$;
    this.dismissedTargets$ = this.migrationMapService.migrationMapTargetsDismissed$;
    this.targetSection = 'listOfTargets';
    this.confirmedDrivers.drivers = [];
  }

  public targetClickedReceiver(targetIdReceived: number) {
    this.targets$ = this.targets$.pipe(
      map((targets) => targets.filter((target) => target.sandDemandSnapshotId === targetIdReceived)),
    );
  }

  public setNoShowYesterday() {
    this.dashboardService.driversHitchCalendar$
      .pipe(
        takeUntil(this.destroy$$),
        filter((data) => !!data),
      )
      .subscribe((resp) => {
        this.driverHitchCalenderData = resp;
        this.driversHitchCalendarWeeks$$.next(chunk(resp, 7));
      });

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    this.driversHitchCalendarWeeks$$.pipe(take(2)).subscribe((weeks) => {
      for (const week of weeks) {
        for (const day of week) {
          if (
            yesterday.getMonth() === new Date(day.date).getMonth() &&
            yesterday.getDate() === new Date(day.date).getDate()
          ) {
            if (day.noShowDrivers) {
              this.noShowYesterday = day.noShowDrivers.length;
              return;
            }
          }
        }
      }
    });
  }

  public showRevenueCalculation() {
    this.matDialog.open(this.revenueCalculationModal, {
      autoFocus: false,
      panelClass: ['w-full', 'h-1/2', 'md:w-1/3', 'lg:w-1/6'],
    });
  }

  public showDriverSelection() {
    this.matDialog.open(this.driverSelectionModal, {
      autoFocus: false,
      panelClass: ['w-full', 'h-1/2', 'md:w-1/2', 'lg:w-1/4'],
    });
  }

  public sortTargets() {
    this.targets$ = this.targets$.pipe(
      map((data) => {
        data.sort((a, b) => {
          if (a.sandDemandSnapshotId > b.sandDemandSnapshotId) {
            return -1;
          }
          return 1;
        });
        return data;
      }),
    );
  }

  public tooltip(driver) {
    return `${parsePhoneNumberFromString(driver.driverPhone).formatNational()}`;
  }

  public mapClickedReceiver() {
    this.setupDrivers();
    this.targets$ = this.migrationMapService.migrationMapTargets$;
    this.sortTargets();
  }

  private setupForm() {
    this.exportGroup = this.fb.group({
      start: [null],
      end: [null],
    });
  }

  public async downloadExport() {
    const url = await this.dashboardService.getExport(
      this.exportGroup?.value?.start?.toISOString(),
      this.exportGroup?.value?.end?.toISOString(),
    );
    if (!!url) {
      window.open(url as string, '_blank');
    }
  }
}
